import { Box, Typography } from "@mui/material";
import { createContext, type FC, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { willFulfillRequest } from "../components/RequestData/RequestsTab/manualAddCompletionUtils";
import { useManualAddCompletion } from "../components/RequestData/RequestsTab/useManualAddCompletion";
import SystemDialog from "../components/UIToolkit/Popups/Dialog";
import { FONT_SIZE, INK, STATUS } from "../theme/tokens";
import { formatTimeRange } from "../utils/dateUtils";
import { type OpenShiftRequest, RequestResponsesStatusOptions, RequestStatus } from "../utils/models";
import { isOpenShiftRequest } from "../utils/openShiftMatching";
import { useAnalytics } from "./analytics/hooks";
import { useChosenDepartment } from "./ChosenDepartment";
import { useRequestsContext } from "./RequestsContext";
import { useSnackbar } from "./SnackbarContext";

type SuggestParams = {
	request: OpenShiftRequest;
	/** Analytics source of the add that made the open shift redundant, e.g. "add_nurse_dialog". */
	source: string;
};

/**
 * Prompts to retire `request`; resolves once the scheduler picks either option — `true` when the request
 * was actually closed or reduced, `false` when kept open, already resolved, or the change failed. A caller
 * offering the same request again (one prompt per slot a multi-add fills) stops after a `false`.
 */
type SuggestCloseOpenShift = (params: SuggestParams) => Promise<boolean>;

const CloseOpenShiftSuggestionContext = createContext<SuggestCloseOpenShift | null>(null);

const countPendingPickups = (request: OpenShiftRequest): number =>
	request.message.request_data.filter(({ status }) => status === RequestResponsesStatusOptions.PENDING).length;

/**
 * Hosts the "this open shift is now redundant" prompt at the app root.
 *
 * It must live above every add-staff surface: those dialogs unmount themselves the moment the add
 * succeeds (the day-schedule's `setIsAdd` closes the whole popup), so a prompt rendered inside them
 * would be destroyed before it could ever paint.
 *
 * Confirming reuses the manual-add completion path: a request still needing other staff is
 * decremented by one, and only the last remaining slot cancels the request outright
 * (needed_nurses cannot go to zero).
 */
export const CloseOpenShiftSuggestionProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { staffRequests } = useRequestsContext();
	const { showSnackbar } = useSnackbar();
	const { completeManualAdd, isCompleting } = useManualAddCompletion();
	const [suggestion, setSuggestion] = useState<SuggestParams | null>(null);
	const resolveRef = useRef<(closed: boolean) => void>(undefined);

	// The add refreshes requests just before this prompt opens, so `suggestion.request` is a pre-add
	// snapshot that can go stale while the dialog is on screen. needed_nurses and request_data are
	// written absolutely, so the copy and the confirm both read this — otherwise the dialog can offer
	// to decrement a request the confirm button deletes, or hide a pending-pickup warning it triggers.
	const liveRequest = suggestion ? staffRequests.find(({ id }) => id === suggestion.request.id) : undefined;
	const dialogRequest = liveRequest && isOpenShiftRequest(liveRequest) ? liveRequest : suggestion?.request;

	const finish = useCallback((closed: boolean) => {
		setSuggestion(null);
		resolveRef.current?.(closed);
		resolveRef.current = undefined;
	}, []);

	const suggestCloseOpenShift = useCallback<SuggestCloseOpenShift>(
		({ request, source }) => {
			track("close_open_shift_suggestion_shown", { source, request_id: request.id, needed_nurses: request.needed_nurses });
			setSuggestion({ request, source });
			return new Promise<boolean>((resolve) => {
				// A newer prompt replaces one still open; the replaced one closed nothing.
				resolveRef.current?.(false);
				resolveRef.current = resolve;
			});
		},
		[track],
	);

	// Backdrop/Escape route here too, so ignore it mid-close — the request is already on its way out.
	const handleKeepOpen = useCallback(() => {
		if (!suggestion || isCompleting) return;
		track("close_open_shift_suggestion_kept_open", { source: suggestion.source, request_id: suggestion.request.id });
		finish(false);
	}, [suggestion, isCompleting, track, finish]);

	const handleCloseOpenShift = useCallback(() => {
		if (!suggestion) return;
		const event = { source: suggestion.source, request_id: suggestion.request.id };
		if (!liveRequest || !isOpenShiftRequest(liveRequest) || liveRequest.status !== RequestStatus.Active) {
			track("close_open_shift_suggestion_already_resolved", event);
			showSnackbar(t("open.shift.suggestion.already.resolved"), "info");
			finish(false);
			return;
		}
		// A newer prompt can replace this one while the change is in flight (it already resolved this one's
		// caller with `false`). Only finish if this prompt is still the current one, or the late result would
		// dismiss the newer prompt and answer its caller with this request's outcome.
		const resolveThisPrompt = resolveRef.current;
		const finishThisPrompt = (closed: boolean) => {
			if (resolveRef.current === resolveThisPrompt) finish(closed);
		};
		completeManualAdd({ request: liveRequest, approvedNursesCount: 0, source: suggestion.source, suppressAddedSnackbar: true })
			// Counted once the request actually changed, so a failed confirm can't read as a close.
			.then(() => {
				track("close_open_shift_suggestion_closed", { ...event, needed_nurses: liveRequest.needed_nurses });
				return true;
			})
			// useDeleteShift / useUpdateNursesNeeded already surface and log a failure; the nurse is
			// added either way, so let the scheduler out of the dialog instead of trapping them in it.
			.catch((error: Error) => {
				trackError("close_open_shift_suggestion_error", { ...event, error_message: error?.message });
				return false;
			})
			.then(finishThisPrompt);
	}, [suggestion, track, trackError, completeManualAdd, finish, liveRequest, showSnackbar, t]);

	const job = dialogRequest?.job_id ? chosenDepartment.jobs?.find(({ id }) => id === dialogRequest.job_id) : undefined;
	const hours = dialogRequest ? formatTimeRange(dialogRequest.message.shift_start, dialogRequest.message.shift_end) : "";
	// Same predicate completeManualAdd uses to pick cancel-vs-decrement, so the copy can never
	// promise something other than what the confirm button actually does.
	const closesRequest = dialogRequest ? willFulfillRequest(0, dialogRequest.needed_nurses) : false;
	// Only the cancel branch declines pending pickups and notifies those staff; decrementing doesn't.
	const pendingPickups = closesRequest && dialogRequest ? countPendingPickups(dialogRequest) : 0;
	const details = [job?.displayName, dialogRequest?.location, hours].filter(Boolean).join(" · ");

	return (
		<CloseOpenShiftSuggestionContext.Provider value={suggestCloseOpenShift}>
			{children}
			{suggestion && dialogRequest && (
				<SystemDialog
					dataTestId="close-redundant-open-shift-dialog"
					open={true}
					onClose={handleKeepOpen}
					title={t(closesRequest ? "close.open.shift.suggestion.title" : "reduce.open.shift.suggestion.title")}
					body={
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>
								{closesRequest ? t("close.open.shift.suggestion.body") : t("reduce.open.shift.suggestion.body", { needed: dialogRequest.needed_nurses })}
							</Typography>
							<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{details}</Typography>
							{pendingPickups > 0 && (
								<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: STATUS.WARNING.dark }}>
									{t("close.open.shift.suggestion.pending.pickups", { count: pendingPickups })}
								</Typography>
							)}
						</Box>
					}
					confirm={t(closesRequest ? "close.open.shift.suggestion.confirm" : "reduce.open.shift.suggestion.confirm")}
					confirmButtonVariant="primary"
					confirmLoading={isCompleting}
					cancel={t("close.open.shift.suggestion.cancel")}
					onConfirm={handleCloseOpenShift}
				/>
			)}
		</CloseOpenShiftSuggestionContext.Provider>
	);
};

export const useCloseOpenShiftSuggestion = (): SuggestCloseOpenShift => {
	const context = useContext(CloseOpenShiftSuggestionContext);
	if (!context) throw new Error("useCloseOpenShiftSuggestion must be used within a CloseOpenShiftSuggestionProvider");
	return context;
};
